// Twenty shell design tokens — read live from the shell's CSS custom
// properties, not copied as hex.
//
// The eWings standalone pages sit inside Twenty's shell, next to record
// tables and settings screens. The front-component renderer wraps every
// component in Twenty's ThemeProvider, so the `--t-*` variables the shell
// itself uses are in scope here. Referencing them directly means:
//   - light/dark follow the host automatically (no scheme comparison bugs),
//   - the eWings palette override (accent ramp) reaches these pages too,
//   - any future shell token change propagates for free.
//
// An earlier version of this file held hex copies and rem sizes. Twenty's
// root font-size is 13px, so `.85rem` rendered 11px where the tables render
// 13px, and captions came out at 8px -- which is what made the pages look
// like a different product.

const v = (name: string) => `var(--t-${name})`;
export { v };

/** --t-font-size-*. The shell's tables use xs (13px on Twenty's 13px root). */
export const SHELL_FONT = {
  xxs: v('font-size-xxs'),
  xs: v('font-size-xs'),
  sm: v('font-size-sm'),
  md: v('font-size-md'),
  lg: v('font-size-lg'),
  xl: v('font-size-xl'),
  xxl: v('font-size-xxl'),
} as const;

/** --t-font-weight-*. Twenty has only three; it never uses 700. */
export const SHELL_WEIGHT = {
  regular: v('font-weight-regular'),
  medium: v('font-weight-medium'),
  semiBold: v('font-weight-semi-bold'),
} as const;

/** --t-border-radius-*. */
export const SHELL_RADIUS = {
  xs: v('border-radius-xs'),
  sm: v('border-radius-sm'),
  md: v('border-radius-md'),
  lg: v('border-radius-lg'),
  xl: v('border-radius-xl'),
  pill: v('border-radius-pill'),
  round: v('border-radius-rounded'),
} as const;

/** --t-spacing-N, where N is a 4px step. */
export const SHELL_SPACE = {
  0: v('spacing-0'),
  half: v('spacing-0_5'),
  1: v('spacing-1'),
  1.5: v('spacing-1_5'),
  2: v('spacing-2'),
  3: v('spacing-3'),
  4: v('spacing-4'),
  5: v('spacing-5'),
  6: v('spacing-6'),
  8: v('spacing-8'),
  10: v('spacing-10'),
  12: v('spacing-12'),
} as const;

export const SHELL_FONT_FAMILY = v('font-family');

export type ShellColors = {
  text: string;
  textSecondary: string;
  textTertiary: string;
  background: string;
  backgroundSecondary: string;
  backgroundTertiary: string;
  backgroundQuaternary: string;
  borderLight: string;
  borderMedium: string;
  borderStrong: string;
};

/**
 * Text and surface colours. One set for both schemes: the variables resolve
 * to the host's current light or dark values at render time.
 */
export const SHELL_COLORS: ShellColors = {
  text: v('font-color-primary'),
  textSecondary: v('font-color-secondary'),
  textTertiary: v('font-color-tertiary'),
  background: v('background-primary'),
  backgroundSecondary: v('background-secondary'),
  backgroundTertiary: v('background-tertiary'),
  backgroundQuaternary: v('background-quaternary'),
  borderLight: v('border-color-light'),
  borderMedium: v('border-color-medium'),
  borderStrong: v('border-color-strong'),
};

/**
 * Kept for call-site compatibility: pages still pass the SDK's colour scheme,
 * but the values no longer depend on it.
 */
export const getShellColors = (_scheme: string): ShellColors => SHELL_COLORS;

/**
 * Text styles matching the shell's own hierarchy. Twenty's root font-size is
 * 13px, so its rem tokens resolve to: xxs 8px · xs 11px · sm 12px · md 13px ·
 * lg 16px · xl 20px. The tables, sidebar and settings all run at md (13px);
 * an earlier version used xs for body and xxs for captions, which rendered
 * these pages at 11px / 8px beside 13px everywhere else.
 */
export const SHELL_TYPE = {
  pageTitle: {
    fontSize: SHELL_FONT.lg,
    fontWeight: SHELL_WEIGHT.semiBold,
    lineHeight: 1.3,
  },
  sectionTitle: {
    fontSize: SHELL_FONT.md,
    fontWeight: SHELL_WEIGHT.semiBold,
    lineHeight: 1.4,
  },
  body: {
    fontSize: SHELL_FONT.md,
    fontWeight: SHELL_WEIGHT.regular,
    lineHeight: 1.5,
  },
  bodyMedium: {
    fontSize: SHELL_FONT.md,
    fontWeight: SHELL_WEIGHT.medium,
    lineHeight: 1.5,
  },
  label: {
    fontSize: SHELL_FONT.md,
    fontWeight: SHELL_WEIGHT.medium,
    lineHeight: 1.4,
  },
  caption: {
    fontSize: SHELL_FONT.xs,
    fontWeight: SHELL_WEIGHT.regular,
    lineHeight: 1.4,
  },
  /** Figure on a stat tile: the record-title size, not a display size. */
  metric: {
    fontSize: SHELL_FONT.lg,
    fontWeight: SHELL_WEIGHT.semiBold,
    lineHeight: 1.2,
  },
} as const;
